import React, { useRef, useLayoutEffect, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import mapboxgl from "mapbox-gl";

import { devicesActions, applicationsActions } from "../store";

import { SwitcherControl } from "./switcher/switcher";
import "./switcher/switcher.css";

import styles from "../../styles/map.module.css";

import { googleSatellite, googleStreet } from "./mapStyles";
import Sidebar from "./sidebar/Sidebar";
import pulsingDot from "./pulsingDot";

const element = document.createElement("div");
element.style.width = "100%";
element.style.height = "100%";

mapboxgl.accessToken = "pk.eyJ1IjoicmFkeWFiZSIsImEiOiJjbWpiOHV5YW4wMDJpM2dzbXFzeWRvNXVhIn0.yspYAIiV5mOuZPpozLaVtw";
mapboxgl.setRTLTextPlugin("js/mapbox-gl-rtl-text.js");

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
export const map = new mapboxgl.Map({
    container: element,
    style: googleStreet(),
    center: [52.9088, 32.8927],
    zoom: 5,
    trackResize: true,
    maxZoom: 17,
    attributionControl: false,
    testMode: true,
});

var popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
});

const readyListeners = new Set();
let ready = false;

const addReadyListener = (listener) => {
    readyListeners.add(listener);
    listener(ready);
};

const removeReadyListener = (listener) => {
    readyListeners.delete(listener);
};

const updateReadyValue = (value) => {
    ready = value;
    readyListeners.forEach((listener) => listener(value));
};

map.addControl(
    new mapboxgl.NavigationControl({
        showCompass: false,
    }),
    "top-left"
);

map.addControl(
    new SwitcherControl(
        [
            { title: "ماهواره", uri: googleSatellite() },
            { title: "نقشه", uri: googleStreet() },
        ],
        "نقشه",
        () => updateReadyValue(false),
        () => {
            const waiting = () => {
                if (!map.loaded()) {
                    setTimeout(waiting, 100);
                } else {
                    initMap();
                }
            };
            waiting();
        }
    ),
    "top-left"
);

const addImage = (name, path) => {
    new Promise((resolve, _reject) => {
        if (!map.hasImage(name))
            map.loadImage(path, (error, start) => {
                if (error) {
                    return;
                }
                if (!map.hasImage(name)) map.addImage(name, start);
                resolve();
            });
    });
};

const initMap = () => {
    new Promise(() => {
        const img = pulsingDot("255, 200, 200", "rgba(255, 100, 100, 1)", 100);
        if (!map.hasImage("offline")) map.addImage("offline", img, { pixelRatio: 2 });
    });

    new Promise(() => {
        const img = pulsingDot("200, 255, 200", "rgba(100, 255, 100, 1)", 100);
        if (!map.hasImage("online")) map.addImage("online", img, { pixelRatio: 2 });
    });

    new Promise(() => {
        const img = pulsingDot("38, 60, 153", "rgb(38, 60, 153, 1)", 100);
        if (!map.hasImage("unknown")) map.addImage("unknown", img, { pixelRatio: 2 });
    });

    new Promise(() => {
        if (!map.getSource("routes"))
            map.addSource("routes", {
                type: "geojson",
                lineMetrics: true,
                data: {
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: "LineString",
                        coordinates: [],
                    },
                },
            });
    });

    new Promise(() => {
        if (!map.getSource("devices"))
            map.addSource("devices", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
    });

    new Promise(() => {
        if (!map.getSource("devices-many"))
            map.addSource("devices-many", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
    });

    new Promise(() => {
        if (!map.getSource("route-arrows"))
            map.addSource("route-arrows", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
    });

    new Promise(() => {
        if (!map.getSource("route-icons"))
            map.addSource("route-icons", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
    });

    new Promise(() => {
        if (!map.getSource("player-car"))
            map.addSource("player-car", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
    });

    new Promise(() => {
        if (!map.getLayer("devices"))
            map.addLayer({
                id: "devices",
                type: "symbol",
                source: "devices",

                layout: {
                    "icon-image": "{icon}",
                    "icon-allow-overlap": true,
                    "text-field": "{name}",
                    "text-allow-overlap": true,
                    "text-anchor": "bottom",
                    "text-offset": [0, -2],
                    "text-font": ["Dana Regular"],
                    "text-size": 12,
                    "symbol-sort-key": ["to-number", ["get", "priority"]],
                    // "icon-size": ["to-number", ["get", "iconSize"]],
                    "icon-offset": ["get", "offset"],
                    "icon-rotate": ["to-number", ["get", "rotation"]],
                },
                paint: {
                    "text-halo-color": "white",
                    "text-halo-width": 1,
                },
            });
    });

    new Promise(() => {
        if (!map.getLayer("devices-many"))
            map.addLayer({
                id: "devices-many",
                type: "circle",
                source: "devices-many",
                paint: {
                    "circle-radius": ["get", "size"],
                    "circle-color": ["get", "color"],
                    "circle-stroke-color": "rgb(255, 255, 255)",
                    "circle-stroke-width": 2.5,
                },
            });
    });

    new Promise(() => {
        if (!map.getLayer("route-arrows"))
            map.loadImage("./images/arrow.png", (error, image) => {
                if (error) return;
                map.addImage("arrow", image);
            });
    });

    new Promise(() => {
        if (!map.getLayer("player-car"))
            map.addLayer({
                id: "player-car",
                type: "symbol",
                source: "player-car",
                layout: {
                    "icon-allow-overlap": true,
                    "icon-image": "{icon}",
                    "icon-size": ["to-number", ["get", "iconSize"]],
                    "icon-rotate": ["to-number", ["get", "rotation"]],
                    "icon-rotation-alignment": "map",
                },
            });

        if (!map.getLayer("route-arrows"))
            map.addLayer(
                {
                    id: "route-arrows",
                    type: "symbol",
                    source: "route-arrows",
                    layout: {
                        "icon-allow-overlap": true,
                        "icon-image": "arrow",
                        "icon-size": 0.06,
                        "icon-rotate": ["to-number", ["get", "rotation"]],
                    },
                },
                "player-car"
            );

        if (!map.getLayer("routes"))
            map.addLayer(
                {
                    id: "routes",
                    type: "line",
                    source: "routes",
                    paint: {
                        "line-color": "red",
                        "line-width": 7,
                        "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, "blue", 0.1, "royalblue", 0.3, "cyan", 0.5, "lime", 0.7, "yellow", 1, "red"],
                    },
                    layout: {
                        "line-cap": "round",
                        "line-join": "round",
                    },
                },
                "route-arrows"
            );

        if (!map.getLayer("route-icons"))
            map.addLayer(
                {
                    id: "route-icons",
                    type: "symbol",
                    source: "route-icons",
                    layout: {
                        "icon-allow-overlap": true,
                        "icon-image": "{icon}",
                        "icon-size": ["to-number", ["get", "iconSize"]],
                        "icon-offset": ["get", "offset"],
                    },
                },
                "player-car"
            );
    });

    addImage("end", "./images/end.png");
    addImage("stop", "./images/stop.png");
    addImage("start", "./images/start.png");

    addImage("redCar", "./images/car-red.png");
    addImage("greenCar", "./images/car-green.png");
    addImage("yellowCar", "./images/car-yellow.png");

    updateReadyValue(true);
};

map.addControl(
    new mapboxgl.AttributionControl({
        compact: true,
    })
);

let popupInterval;

const showPopup = (e) => {
    if (popupInterval) clearTimeout(popupInterval);

    popup.remove();

    var coordinates = e.features[0].geometry.coordinates.slice();
    var description = e.features[0].properties.description;

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    popup.setLngLat(coordinates).setHTML(description).addTo(map);

    popupInterval = setTimeout(() => {
        popup.remove();
    }, 3000);
};

map.on("mouseenter", "route-arrows", showPopup);
map.on("click", "route-arrows", showPopup);

map.on("mouseenter", "route-icons", showPopup);
map.on("click", "route-icons", showPopup);

map.on("load", initMap);

const Map = ({ children }) => {
    const containerEl = useRef(null);
    const [mapReady, setMapReady] = useState(false);

    const HaveRoute = useSelector((state) => state.applications.haveRoute);
    const user = useSelector((state) => state.session.user);

    const dispatch = useDispatch();

    useEffect(() => {
        const listener = (ready) => setMapReady(ready);
        addReadyListener(listener);
        return () => {
            removeReadyListener(listener);
        };
    }, []);

    useLayoutEffect(() => {
        const currentEl = containerEl.current;
        currentEl.appendChild(element);
        if (map) {
            map.resize();
        }

        return () => {
            currentEl.removeChild(element);
        };
    }, [containerEl]);

    useLayoutEffect(() => {
        const Do = (e) => {
            new Promise(() => {
                dispatch(devicesActions.select(e.features[0].properties.id));
                dispatch(applicationsActions.dialog("Search"));
            });
            new Promise(() => {
                map.flyTo({ center: JSON.parse(e.features[0].properties.position), zoom: 14 });
            });
        };
        map.on("click", "devices", Do);
        map.on("click", "devices-many", Do);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        map.resize();
    }, [HaveRoute]);

    return (
        <div className={styles.main}>
            <div className={styles.left} style={{ height: "100%" }}></div>

            <div className={styles.map} ref={containerEl} style={{ height: "calc(100% - " + (70 + (user.administrator ? 60 : 0) + (HaveRoute ? 60 : 0)) + "px" }}>
                {mapReady && children}
            </div>
            <Sidebar />
        </div>
    );
};

export default Map;
